import {RangeFilter} from "./RangeFilter";
import {RatingFilter} from "./RatingFilter";
import {CheckboxFilter} from "./CheckboxFilter";
import {useMemo} from "@wordpress/element";
import {SelectFilter} from "./SelectFilter";
import {SearchFilter} from "./SearchFilter";
import {MultiCheckboxFilter} from "./MultiCheckboxFilter";
import {CategoryFilter} from "./CategoryFilter";

const Filters = {
    "select": SelectFilter,
    "range": RangeFilter,
    "rating": RatingFilter,
    "checkbox": CheckboxFilter,
    "search": SearchFilter,
    "multi-checkbox": MultiCheckboxFilter,
    "category": CategoryFilter,
};

export const FilterRenderer = ({config, onChange, value}) => {
    const FilterComponent = useMemo(() => Filters[config.type], [config.type]);
    if (!FilterComponent || config.hidden) return null;
    return <FilterComponent config={config} onChange={onChange} value={value}/>;
};

export const getFilterChip = (config, value) => {
    if (config.chipHidden) return "";
    switch (config.type) {
        case 'select': {
            const option = config.options?.options?.find(o => o.value === value);
            return option ? `${config.label}: ${option.label}` : '';
        }
        case 'search': {
            if (Array.isArray(value)) {
                if (!value.length) return [];
                return value.map(v => ({label: `${config.label}: ${v}`, value: v}));
            }
            return value ? [{label: `${config.label}: ${value}`, value}] : [];
        }
        case 'range': {
            const [min, max] = Array.isArray(value) ? value : [];
            if (min != null && max != null) {
                return `${config.label}: $${min} – $${max}`;
            } else if (min != null) {
                return `${config.label}: ≥ $${min}`;
            } else if (max != null) {
                return `${config.label}: ≤ $${max}`;
            }
            return "";
        }
        case 'rating': {
            if (value === 'top') return 'Top Rated';
            return `${config.label}: ${value}+ Stars`;
        }
        case 'checkbox': {
            return config.label;
        }
        case 'category': {
            return value?.name ? `Category: ${value.name}` : '';
        }
        case 'multi-checkbox': {
            if (Array.isArray(value)) {
                if (!value.length) return [];
                return value.map(v => {
                    const opt = config.options?.find(o => o.value === v);
                    const label = opt ? opt.label : v;
                    return {label: `${config.label}: ${label}`, value: v};
                });
            }
            return [];
        }
    }
    return "";
};

export const getAppliedFilters = (filterConfigs, filterValues) => {
    return filterConfigs.reduce((acc, config) => {
        const value = filterValues[config.id];
        if (!value) {
            return acc;
        }

        switch (config.type) {
            case 'range':
                if (Array.isArray(value)) {
                    const [min, max] = value;
                    if (typeof min === 'number') {
                        acc.push({
                            field: config.field,
                            operator: 'GREATER_THAN_OR_EQUALS',
                            value: String(min),
                        });
                    }
                    if (typeof max === 'number') {
                        acc.push({
                            field: config.field,
                            operator: 'LESS_THAN_OR_EQUALS',
                            value: String(max),
                        });
                    }
                }
                break;

            case 'rating':
                if (value === 'top') {
                    acc.push({
                        field: 'customerTopRated',
                        operator: 'EQUALS',
                        value: 'true',
                    });
                } else if (value > 0) {
                    acc.push({
                        field: config.field,
                        operator: 'GREATER_THAN_OR_EQUALS',
                        value: String(value),
                    });
                }
                break;

            case 'checkbox':
                if (value) {
                    acc.push({
                        field: config.field,
                        operator: 'EQUALS',
                        value: 'true',
                    });
                }
                break;

            case 'select':
                if (value) {
                    acc.push({
                        field: config.field,
                        operator: 'EQUALS',
                        value,
                    });
                }
                break;

            case 'category':
                if (value?.id) {
                    acc.push({
                        field: 'categoryId',
                        operator: 'EQUALS',
                        value: value.id,
                    });
                }
                break;

            case 'multi-checkbox':
                if (Array.isArray(value) && value.length > 0) {
                    const standardValues = [];
                    value.forEach(v => {
                        const opt = config.options?.find(o => o.value === v);
                        if (opt?.filter) {
                            acc.push({
                                field: opt.filter.field,
                                operator: opt.filter.operator,
                                value: String(opt.filter.value),
                            });
                        } else {
                            standardValues.push(v);
                        }
                    });

                    if (standardValues.length > 0) {
                        acc.push({
                            field: config.field,
                            operator: 'IN',
                            value: standardValues.join(','),
                        });
                    }
                }
                break;

            case 'search': {

                if (Array.isArray(value)) {
                    const cleaned = value.filter(v => !!v);
                    if (cleaned.length) {
                        acc.push({
                            field: config.field,
                            operator: 'IN',
                            value: cleaned.join(','),
                        });
                    }
                } else if (value) {
                    acc.push({
                        field: config.field,
                        operator: 'IN',
                        value,
                    });
                }
                break;
            }
        }

        return acc;
    }, []);
}